import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { useMyRecommendations } from "../../hooks/scout-request/useMyRecommendations";
import { OnRadarPill } from "../../components/scout-request/ScoutPills";
import ScoutAvatar from "../../components/scout-request/ScoutAvatar";
import { formatScoutDateAbsolute } from "../../utils/scoutFormat";
import { formatScoutPosition } from "../../constants/scoutRequestChoices";

// "My recommendations" (design 1n/1o). THE privacy-critical surface: the only status
// that can ever render is the green "On the radar" pill — otherwise the status cell
// is EMPTY. There is deliberately no "pending"/"dismissed" string anywhere in this
// file (Invariant 2); the API doesn't send one and the UI can't invent one.
const RowSkeleton = () => (
  <div className="flex items-center gap-3 py-3 animate-pulse">
    <div className="w-9 h-9 rounded-full bg-gray-100" />
    <div className="flex-1 h-4 bg-gray-100 rounded" />
    <div className="h-4 w-24 bg-gray-100 rounded" />
  </div>
);

// "FC Aventicum · Centre-back · 17–21" — the age band (N-2) now travels on the
// request block from the serializer.
const ageBand = ({ age_min, age_max }) => {
  if (age_min != null && age_max != null) return `${age_min}–${age_max}`;
  if (age_min != null) return `${age_min}+`;
  if (age_max != null) return `up to ${age_max}`;
  return "";
};

const RequestSummary = ({ request }) => {
  const parts = [
    request?.club_name,
    formatScoutPosition(request?.position_primary) || request?.position_primary,
    ageBand(request || {}),
  ].filter(Boolean);
  return <span>{parts.join(" · ")}</span>;
};

// W1-03: link the player (name + avatar) to the public player profile. The
// profile route is keyed by the WSPlayer id (/search/player/:wsPlayerId), so
// the link renders exactly when the recommendation carries that id — rows
// without one (e.g. roster picks without a Wyscout link) stay plain text.
const playerProfilePath = (rec) => {
  const wsId = rec?.player_wyscout_id ?? rec?.wyscout_player ?? null;
  return wsId !== null && wsId !== undefined ? `/search/player/${wsId}` : null;
};

const MyRecommendations = () => {
  const navigate = useNavigate();
  const { recommendations, loading, error, fetchRecommendations } = useMyRecommendations();

  return (
    <div className="min-h-screen bg-[#EDF0F4]">
      {/* Platform-standard container (W1-04): no fixed max-width, page
          scales to 2560+ via the progressive padding ladder. */}
      <div className="mx-auto px-4 md:px-6 lg:px-7 xl:px-8 min-[1920px]:px-10 min-[2560px]:px-12 py-6">
        <div className="mb-5">
          <h1 className="text-2xl font-bold text-[#111827]">My recommendations</h1>
          <p className="text-[13px] text-[#6B7280] mt-1">Visible only to you and each club.</p>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-[#E5E7EB] divide-y divide-[#F3F4F6] px-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <RowSkeleton key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center py-20 text-center">
            <p className="text-red-500 mb-3">{error}</p>
            <button onClick={() => fetchRecommendations(1)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
              Try again
            </button>
          </div>
        ) : recommendations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
            <div className="bg-white border border-[#E5E7EB] rounded-full p-4 mb-4">
              <Check className="w-7 h-7 text-[#9CA3AF]" />
            </div>
            <h3 className="text-[17px] font-bold text-[#111827] mb-1.5">No recommendations yet</h3>
            <p className="text-[13px] text-[#6B7280] max-w-sm mb-5">
              Browse scout requests and put players on clubs&apos; radars early. Whoever recommends first keeps
              the credit when it gets serious.
            </p>
            <button
              onClick={() => navigate("/scout-requests")}
              className="px-4 py-2 rounded-lg bg-[#2283FF] text-white text-[13px] font-semibold hover:bg-[#1B6FE0]"
            >
              Browse scout requests
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#E5E7EB] divide-y divide-[#F3F4F6]">
            {recommendations.map((rec) => {
              const profilePath = playerProfilePath(rec);
              return (
              <div
                key={rec.id}
                className="flex items-start sm:items-center gap-3 px-4 py-3"
                data-testid="my-rec-row"
              >
                {profilePath ? (
                  <Link
                    to={profilePath}
                    className="flex-shrink-0"
                    aria-label={rec.player_name}
                  >
                    <ScoutAvatar name={rec.player_name} size={36} />
                  </Link>
                ) : (
                  <ScoutAvatar name={rec.player_name} size={36} />
                )}
                {/* On desktop this reads as one horizontal row; on mobile the request
                    + sent-date drop to a second line so the player name never
                    over-truncates on a narrow screen (design 1v). */}
                <div className="flex-1 min-w-0 text-[13px]">
                  <div className="flex items-center gap-2 min-w-0">
                    {profilePath ? (
                      <Link
                        to={profilePath}
                        className="font-semibold text-[#111827] truncate hover:underline"
                      >
                        {rec.player_name}
                      </Link>
                    ) : (
                      <span className="font-semibold text-[#111827] truncate">{rec.player_name}</span>
                    )}
                    <ArrowRight className="w-3.5 h-3.5 text-[#9CA3AF] flex-shrink-0 hidden sm:block" />
                    <span className="text-[#6B7280] truncate hidden sm:inline">
                      <RequestSummary request={rec.request} />
                    </span>
                  </div>
                  <p className="sm:hidden text-[12px] text-[#6B7280] mt-0.5 truncate">
                    <RequestSummary request={rec.request} /> · Sent {formatScoutDateAbsolute(rec.created_at)}
                  </p>
                </div>
                <span className="text-[11.5px] text-[#9CA3AF] hidden sm:block flex-shrink-0">
                  Sent {formatScoutDateAbsolute(rec.created_at)}
                </span>
                {/* Status cell: the ONLY value that can appear here is "On the radar".
                    on_radar false → an empty cell, never a pending/dismissed hint. */}
                <div className="flex justify-end flex-shrink-0 sm:w-28" data-testid="my-rec-status">
                  {rec.on_radar ? <OnRadarPill>On the radar</OnRadarPill> : null}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyRecommendations;
